/** Read display of the live booking state surfaced by an action's detail():
 *  import keys (RESTECH_SOURCE_CONFIG), import-key permissions
 *  (IMPORT_SOURCE_PERMISSIONS), or API credentials (IMPORT_ACCESS_TOKEN_CHECK). The
 *  generic field editor (source_id) + the Run button (untick tours import) handle the
 *  actual edits; this just lists what exists so ops can see it. */
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { PreflightItem } from '@/api/preflight';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { apiCredentialActions, importKeyActions, type TenantCtx } from '../tenantLinks';

interface SourceRow {
  id: number;
  code: string;
  name: string;
}
interface KeyRow {
  id: number;
  code: string;
  name: string;
  grants_tours: boolean;
  /** The catalogue key, which is MEANT to keep tours import on — the action
   *  skips it, so flagging it as a violation would be a standing false alarm. */
  exempt: boolean;
}
interface CredRow {
  id: number;
  name: string;
  scope: string;
  key_prefix: string;
  status: string;
}

export function RestechDetailDisplay({
  item,
  topId,
  toCode,
}: {
  item: PreflightItem;
  topId: number;
  toCode: string;
}) {
  const d = item.detail ?? {};
  const ctx: TenantCtx = { id: topId, code: toCode };

  if (item.action === 'RESTECH_SOURCE_CONFIG') {
    const sources = (d.sources as SourceRow[] | undefined) ?? [];
    const current = d.current_source_id;
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Import keys ({sources.length})
        </Typography>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {sources.length === 0 ? (
            <Typography variant="body2">No import keys for this operator.</Typography>
          ) : (
            sources.map((s) => (
              <Typography key={s.id} variant="body2">
                {s.name || '—'} ·{' '}
                <ValueActions actions={importKeyActions(s.code, ctx)}>
                  <code>{s.code}</code>
                </ValueActions>
                {current === s.code && <Chip size="small" color="success" label="current" sx={{ ml: 1 }} />}
              </Typography>
            ))
          )}
        </Stack>
      </Box>
    );
  }

  if (item.action === 'IMPORT_SOURCE_PERMISSIONS') {
    const rows = (d.sources as KeyRow[] | undefined) ?? [];
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Import keys ({rows.length})
        </Typography>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {rows.length === 0 ? (
            <Typography variant="body2">No import keys for this operator.</Typography>
          ) : (
            rows.map((r) => (
              <Typography key={r.id} variant="body2">
                {r.name || '—'} ·{' '}
                <ValueActions actions={importKeyActions(r.code, ctx)}>
                  <code>{r.code}</code>
                </ValueActions>{' '}
                {r.exempt ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="catalogue key — keeps tours import"
                    sx={{ ml: 0.5 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    color={r.grants_tours ? 'warning' : 'success'}
                    label={r.grants_tours ? 'grants tours import' : 'tours import off'}
                    sx={{ ml: 0.5 }}
                  />
                )}
              </Typography>
            ))
          )}
        </Stack>
      </Box>
    );
  }

  if (item.action === 'IMPORT_ACCESS_TOKEN_CHECK') {
    const creds = (d.api_credentials as CredRow[] | undefined) ?? [];
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          API credentials ({creds.length})
        </Typography>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {creds.length === 0 ? (
            <Typography variant="body2">No API credentials for this operator.</Typography>
          ) : (
            creds.map((c) => (
              <Typography key={c.id} variant="body2">
                <ValueActions actions={apiCredentialActions(c, ctx)}>{c.name}</ValueActions> ·{' '}
                {c.scope} · <code>{c.key_prefix}…</code>{' '}
                <Chip
                  size="small"
                  color={c.status === 'active' ? 'success' : 'default'}
                  label={c.status}
                  sx={{ ml: 0.5 }}
                />
              </Typography>
            ))
          )}
        </Stack>
      </Box>
    );
  }

  return null;
}
